import { memo, useState, useCallback, useRef } from "react";
import { NavLink } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import type { NavItem } from "./sidebarData";

interface SidebarItemProps {
  item: NavItem;
  collapsed: boolean;
  isActive: boolean;
}

export const SidebarItem = memo(function SidebarItem({
  item,
  collapsed,
  isActive,
}: SidebarItemProps) {
  const queryClient = useQueryClient();
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const Icon = item.icon;
  const label = item.label;

  const handleMouseEnter = useCallback(() => {
    if (collapsed) {
      tooltipTimeout.current = setTimeout(() => setShowTooltip(true), 200);
    }
    item.prefetch?.(queryClient);
  }, [collapsed, item, queryClient]);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(tooltipTimeout.current);
    setShowTooltip(false);
  }, []);

  return (
    <div className="relative">
      <NavLink
        to={item.path}
        aria-label={label}
        aria-current={isActive ? "page" : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        className={cn(
          "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-medium transition-[color,background-color,border-color,box-shadow,opacity,transform,translate,scale,rotate] duration-200",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary focus-visible:shadow-none",
          collapsed && "justify-center px-0 py-2.5",
          isActive
            ? "bg-primary/[0.08] text-primary dark:bg-primary/[0.10]"
            : "text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
        )}
      >
        {isActive && (
          <span
            key="active-indicator"
            className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-gradient-to-b from-primary via-primary to-accent"
            style={{
              boxShadow: "0 0 12px var(--primary-30)",
            }}
          />
        )}

        <span
          key="icon"
          className={cn(
            "flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg transition-[color,background-color,border-color,box-shadow,opacity,transform,translate,scale,rotate] duration-200",
            isActive
              ? "text-primary bg-primary/[0.10] shadow-[0_0_12px_var(--primary-8)]"
              : "text-sidebar-foreground/70 group-hover:text-sidebar-foreground group-hover:bg-sidebar-accent/50",
          )}
        >
          <Icon size={16} strokeWidth={isActive ? 2.2 : 1.8} />
        </span>

        {!collapsed && (
          <span
            key="label"
            className={cn(
              "flex-1 truncate transition-colors duration-200",
              isActive && "font-semibold",
            )}
          >
            {label}
          </span>
        )}

        {item.badge === "ai" && !collapsed && (
          <span key="badge" className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary shadow-[0_0_6px_var(--primary-40)]" />
          </span>
        )}
      </NavLink>

      {collapsed && showTooltip && (
        <div
          role="tooltip"
          className="absolute left-full top-1/2 -translate-y-1/2 ml-3 z-50 pointer-events-none"
        >
          <div className="rounded-xl bg-popover px-3.5 py-2 text-[13px] font-medium text-popover-foreground shadow-overlay border border-border/50 whitespace-nowrap animate-fade-in">
            {label}
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-[5px] border-transparent border-r-popover" />
          </div>
        </div>
      )}
    </div>
  );
});
